import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    ChevronRight, ArrowRight, CheckCircle2, ChevronDown,
    Building, Mail, Phone, User, Users, Briefcase,
    Globe, HelpCircle, MapPin, Star, Award, Layers,
    Shield, FileSpreadsheet, Scale, Cpu, Zap, TrendingUp, ShieldCheck
} from 'lucide-react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { port } from '../../App';
import { toast } from 'react-toastify';
import serviceBannerImg from '../../Assest/cards/serviceBanner.avif';
import GlassHeroSection from './GlassHeroSection';
import CTAButton from '../CTAButton';
import recruitmentImg from '../../Assest/cards/recruitment-consulting-firm.avif';

import itImg from '../../Assest/cards/our service/it-software.avif';
import healthcareImg from '../../Assest/cards/our service/Healthcare.avif';
import manufacturingImg from '../../Assest/cards/our service/Manufacturing.avif';
import bfsiImg from '../../Assest/cards/our service/BFSI.avif';
import ecommerceImg from '../../Assest/cards/our service/Ecommerce.avif';
import logisticsImg from '../../Assest/cards/our service/logistics-supply-chain.avif';
import startupsImg from '../../Assest/cards/our service/Startups.avif';
import gccsImg from '../../Assest/cards/our service/GCCs.avif';

const MainServicesPage = () => {
    const navigate = useNavigate();
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const [formData, setFormData] = useState({
        "Full Name": "",
        "Company Name": "",
        "Business Email": "",
        "Phone Number": "",
        "Services Required": "Staffing & Recruitment Services",
        "Hiring Volume": "1-10 Positions",
        "Message": ""
    });
    const [loading, setLoading] = useState(false);
    const [openFaq, setOpenFaq] = useState(null);

    const toggleFaq = (index) => {
        setOpenFaq(openFaq === index ? null : index);
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const payload = {
                ...formData,
                source_page: 'Main Services Page'
            };
            await axios.post(`${port}/api/page-enquiry/`, payload);
            toast.success("Enquiry submitted successfully! Our strategy team will reach out shortly.");
            setFormData({
                "Full Name": "",
                "Company Name": "",
                "Business Email": "",
                "Phone Number": "",
                "Services Required": "Staffing & Recruitment Services",
                "Hiring Volume": "1-10 Positions",
                "Message": ""
            });
        } catch (error) {
            console.error("Error submitting enquiry:", error);
            toast.error("Failed to submit enquiry. Please check your network and try again.");
        } finally {
            setLoading(false);
        }
    };

    const scrollToForm = () => {
        const element = document.getElementById('enquiry-section');
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const servicesList = [
        {
            title: "Staffing & Recruitment Services",
            icon: <Users size={32} />,
            desc: "Build agile, scalable, and business-ready teams through customized staffing services designed for modern workforce requirements. As a trusted staffing and recruitment company, we help businesses streamline hiring operations, reduce recruitment delays, improve candidate quality, and build high-performing teams across multiple industries.",
            desc2: "Our expert recruiters specialize in delivering reliable contract staffing services, permanent staffing solutions, IT staffing, non-IT staffing, and bulk hiring solutions tailored to your business goals. Whether you are a startup, SME, or enterprise organization, our recruitment specialists help you hire faster, reduce workforce gaps, and improve overall hiring efficiency.",
            desc3: "With strong industry expertise, advanced sourcing strategies, and a vast talent network, we help organizations attract skilled professionals while reducing recruitment costs and improving workforce productivity.",
            included: [
                "Contract Staffing Services",
                "Permanent Staffing Services",
                "IT Staffing Services",
                "Non-IT Staffing Services",
                "Bulk Hiring Services"
            ],
            slug: "staffing",
            ctaText: "Explore Staffing Solutions"
        },
        {
            title: "Specialized Recruitment Services",
            icon: <Award size={32} />,
            desc: "Hire highly skilled professionals through strategic specialized recruitment services designed for leadership hiring, startup expansion, global recruitment, and modern workforce requirements.",
            desc2: "Our recruitment experts help businesses source qualified candidates for niche roles, executive positions, remote teams, and international hiring requirements through structured and result-driven recruitment strategies.",
            desc3: "From executive search services and startup hiring to remote hiring solutions, offshore recruitment, and AI recruitment solutions, we help organizations build future-ready teams that support long-term business growth and operational success.",
            included: [
                "Executive Search Services",
                "GCC Hiring Services",
                "Startup Hiring Services",
                "Campus Recruitment Services",
                "Remote Hiring Services",
                "Offshore Recruitment Services",
                "Virtual Recruitment Services",
                "AI Recruitment Services"
            ],
            slug: "specialized-recruitment",
            ctaText: "Explore Specialized Recruitment Services"
        },
        {
            title: "Recruitment Process Outsourcing (RPO) Services",
            icon: <Layers size={32} />,
            desc: "Transform fragmented hiring operations into scalable and efficient recruitment systems through advanced Recruitment Process Outsourcing (RPO) services designed for growing businesses and enterprise workforce expansion.",
            desc2: "Our RPO solutions help organizations improve hiring consistency, reduce internal HR workload, accelerate talent acquisition, and optimize recruitment performance through structured hiring frameworks.",
            desc3: "As a reliable RPO company, we manage sourcing, screening, interview coordination, onboarding support, and recruitment process management to help businesses improve workforce quality and recruitment efficiency.",
            included: [
                "Recruitment Process Outsourcing Services",
                "Project RPO Services"
            ],
            slug: "rpo",
            ctaText: "Explore RPO Services"
        },
        {
            title: "HR Outsourcing Services",
            icon: <Shield size={32} />,
            desc: "Simplify workforce management with professional HR outsourcing services designed to improve operational efficiency, employee administration, and compliance management.",
            desc2: "Our HR specialists help businesses streamline HR process outsourcing, employee documentation, HR administration, and workforce compliance operations through organized and scalable HR management systems.",
            desc3: "We help organizations reduce administrative workload, improve HR efficiency, and maintain structured workforce operations while ensuring compliance with labor regulations and company policies.",
            included: [
                "HR Administration Services",
                "HR Documentation Services",
                "HR Compliance Services"
            ],
            slug: "hr-outsourcing",
            ctaText: "Explore HR Outsourcing Services"
        },
        {
            title: "Payroll & Compliance Services",
            icon: <FileSpreadsheet size={32} />,
            desc: "Manage payroll operations accurately and efficiently through secure and scalable payroll outsourcing services tailored for modern businesses.",
            desc2: "Our payroll specialists help organizations simplify salary processing, employee payroll management, contractor payroll administration, and workforce compliance operations while reducing payroll errors and administrative complexity.",
            desc3: "As a trusted payroll outsourcing company, we help businesses improve payroll accuracy, maintain workforce transparency, and ensure timely salary processing across all employee categories.",
            included: [
                "Payroll Processing Services",
                "Payroll Outsourcing Services",
                "Contractor Payroll Management Services"
            ],
            slug: "payroll-compliance",
            ctaText: "Explore Payroll & Compliance Solutions"
        },
        {
            title: "Statutory & Compliance Management Services",
            icon: <Scale size={32} />,
            desc: "Reduce legal and compliance risks through reliable statutory compliance services designed to support smooth workforce operations and regulatory adherence.",
            desc2: "Our compliance specialists help businesses manage PF & ESI management, labor law compliance, employee statutory documentation, and workforce-related compliance requirements efficiently.",
            desc3: "With structured compliance systems and proactive workforce management strategies, we help organizations maintain legal compliance, improve operational transparency, and reduce compliance-related challenges.",
            included: [
                "Statutory Compliance Services",
                "PF & ESI Management Services"
            ],
            slug: "statutory-compliance",
            ctaText: "Explore Statutory & Compliance Services"
        }
    ];

    const trustBadges = [
        "AI-Powered Recruitment Workflows",
        "PAN India Hiring Network",
        "Specialized Industry Recruiters",
        "Payroll & Compliance Specialists",
        "GCC & Startup Hiring Support",
        "Scalable Workforce Infrastructure"
    ];

    const frameworkSteps = [
        { step: "STEP 1", title: "Workforce Discovery & Hiring Audit", desc: "We evaluate current hiring gaps, workforce challenges, operational bottlenecks, growth plans, and recruitment inefficiencies." },
        { step: "STEP 2", title: "Workforce Planning Strategy", desc: "Our consultants build customized workforce strategies aligned with business goals, hiring timelines, workforce structure, and operational scalability." },
        { step: "STEP 3", title: "AI-Powered Talent Sourcing", desc: "We leverage advanced sourcing systems, talent databases, recruitment intelligence tools, and strategic hiring channels to identify qualified candidates faster." },
        { step: "STEP 4", title: "Screening & Workforce Evaluation", desc: "Candidates are evaluated through structured screening systems focused on technical capability, experience relevance, role alignment, and organizational fit." },
        { step: "STEP 5", title: "Interview Coordination & Hiring Management", desc: "Our teams manage interview scheduling, stakeholder coordination, hiring communication, offer processing, and recruitment workflow management." },
        { step: "STEP 6", title: "Onboarding & Workforce Integration", desc: "Selected candidates are supported through onboarding coordination, documentation management, workforce integration, and HR operational transition." },
        { step: "STEP 7", title: "Payroll, Compliance & Workforce Support", desc: "We continue supporting businesses through payroll management, compliance execution, workforce administration, and HR operational systems." }
    ];

    const outcomesList = [
        { title: "Faster Hiring Cycles", highlight: "Up To 45% Faster Recruitment Turnaround", desc: "Accelerate workforce expansion through optimized sourcing pipelines, faster candidate screening, and structured recruitment workflows." },
        { title: "Improved Hiring Quality", highlight: "Access To Pre-Screened Talent Networks", desc: "Connect with technically evaluated and role-aligned candidates across IT, non-IT, leadership, operational, and specialized hiring categories." },
        { title: "Workforce Scalability", highlight: "High-Volume Hiring Support Across Multiple Locations", desc: "Scale workforce operations confidently during rapid expansion, project growth, GCC setup, or seasonal hiring demand." },
        { title: "Payroll Accuracy & Compliance", highlight: "Reduced Payroll Errors & Compliance Risks", desc: "Ensure smooth payroll processing, statutory compliance management, PF & ESI handling, and workforce documentation support." },
        { title: "Reduced HR Operational Burden", highlight: "Streamlined HR Administration Systems", desc: "Free internal HR teams from repetitive operational tasks through outsourced workforce management support." },
        { title: "Strategic Workforce Efficiency", highlight: "Better Workforce Planning & Resource Allocation", desc: "Align recruitment strategies with long-term organizational goals for improved productivity and operational stability." }
    ];

    const faqs = [
        { q: "How do AI-powered recruitment solutions improve hiring?", a: "Our AI-supported recruitment systems help businesses streamline sourcing, screening, and hiring workflows for faster and more accurate recruitment execution." },
        { q: "Which industries does Merida HR Consulting specialize in?", a: "We provide specialized HR consulting services for IT & Software, Healthcare, Manufacturing, BFSI, Ecommerce, Logistics, Startups, and GCCs." },
        { q: "Can you support bulk hiring and workforce scaling?", a: "Yes. Our workforce systems are designed to support bulk hiring, startup scaling, GCC expansion, and multi-location recruitment operations." },
        { q: "Do you provide payroll outsourcing and compliance services?", a: "Yes. We provide payroll outsourcing, statutory compliance management, contractor payroll support, PF & ESI management, HR documentation, and workforce administration services." },
        { q: "What makes Merida HR different from traditional staffing firms?", a: "Unlike traditional recruitment agencies, we combine staffing, payroll, compliance, recruitment outsourcing, and workforce strategy under one integrated HR ecosystem." },
        { q: "Do you provide HR consulting services for startups?", a: "Yes. We support startups with scalable hiring systems, workforce planning, payroll management, recruitment outsourcing, and operational HR support." }
    ];

    const industryItems = [
        {
            name: "IT & Software",
            desc: "Technology recruitment across engineering, product, cloud, cybersecurity, ERP, AI, and enterprise systems.",
            image: itImg,
            span: "lg:col-span-2"
        },
        {
            name: "Healthcare",
            desc: "Healthcare staffing support for hospitals, clinics, diagnostics, and healthcare operations.",
            image: healthcareImg,
            span: "lg:col-span-1"
        },
        {
            name: "Manufacturing",
            desc: "Industrial workforce hiring, plant staffing, operational recruitment, and factory workforce scaling.",
            image: manufacturingImg,
            span: "lg:col-span-1"
        },
        {
            name: "BFSI",
            desc: "Banking, finance, insurance, fintech, and financial operations hiring solutions.",
            image: bfsiImg,
            span: "lg:col-span-1"
        },
        {
            name: "Ecommerce",
            desc: "Scalable hiring for ecommerce operations, logistics, customer support, and digital commerce teams.",
            image: ecommerceImg,
            span: "lg:col-span-1"
        },
        {
            name: "Logistics & Supply Chain",
            desc: "Operational staffing and workforce scaling support for logistics ecosystems.",
            image: logisticsImg,
            span: "lg:col-span-2"
        },
        {
            name: "Startups",
            desc: "Agile workforce systems designed for fast-growing startups and emerging businesses.",
            image: startupsImg,
            span: "lg:col-span-2"
        },
        {
            name: "GCCs",
            desc: "Specialized hiring support for Global Capability Center expansion and offshore workforce operations.",
            image: gccsImg,
            span: "lg:col-span-2"
        }
    ];

    return (
        <PageWrapper>
            {/* 3D GLASS HERO SECTION */}
            <GlassHeroSection scrollToForm={scrollToForm} trustBadges={trustBadges} />

            {/* SERVICES LIST SECTION */}
            <Section id="services-section" className="bg-slate-50">
                <div className="container mx-auto px-6">
                    <div className=" mx-auto text-center mb-20">
                        <span className="text-blue-600 font-bold text-xs uppercase block mb-3">
                            Service Lifecycle
                        </span>
                        <h2 className="text-3xl md:text-5xl font-bold text-slate-900 syne leading-tight mb-6">
                            Integrated HR Consulting Services <br />
                            <span className="text-blue-600">Built Across The Complete Employee Lifecycle</span>
                        </h2>
                        <p className="text-slate-500 text-lg md:text-lg leading-relaxed">
                            Modern businesses require more than isolated recruitment support. They need scalable workforce systems capable of supporting hiring, onboarding, payroll operations, compliance management, workforce administration, and long-term workforce scalability under one structured ecosystem.
                        </p>
                    </div>

                    <div className="space-y-16 pb-24">
                        {servicesList.map((service, idx) => {
                            const isNavy = idx % 2 === 1;
                            return (
                                <motion.div
                                    key={idx}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.6 }}
                                    className={`sticky rounded-[3rem] border p-8 md:p-14 shadow-2xl transition-all duration-500 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start ${isNavy
                                        ? 'bg-[#01014c] border-blue-500/20 text-white shadow-[0_30px_60px_rgba(1,1,76,0.25)]'
                                        : 'bg-white border-slate-200/60 text-slate-900 shadow-lg'
                                        }`}
                                    style={{
                                        top: isMobile ? '90px' : '120px',
                                        zIndex: idx + 1
                                    }}
                                >
                                    <div className="lg:col-span-8 space-y-6">
                                        <div className="flex items-center gap-4">
                                            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-colors ${isNavy ? 'bg-white/10 text-blue-400' : 'bg-blue-50 text-blue-600'
                                                }`}>
                                                {service.icon}
                                            </div>
                                            <h3 className={`text-xl md:text-2xl font-bold syne ${isNavy ? 'text-white' : 'text-slate-900'
                                                }`}>{service.title}</h3>
                                        </div>
                                        <p className={isNavy ? 'text-slate-300' : 'text-slate-600'}>{service.desc}</p>
                                        <p className={`text-sm ${isNavy ? 'text-slate-400' : 'text-slate-500'}`}>{service.desc2}</p>
                                        <p className={`text-sm ${isNavy ? 'text-slate-400' : 'text-slate-500'}`}>{service.desc3}</p>

                                        <div className="pt-4">
                                            <button
                                                className={isNavy ? "btn-12-white" : "btn-12-navy"}
                                                onClick={() => navigate(`/service/${service.slug}`)}
                                            >
                                                <span>{service.ctaText} <ArrowRight size={18} className="arrow" /></span>
                                            </button>
                                        </div>
                                    </div>

                                    <div className={`lg:col-span-4 rounded-[2rem] p-6 md:p-8 border ${isNavy
                                        ? 'bg-white/5 border-white/10'
                                        : 'bg-slate-50 border-slate-100'
                                        }`}>
                                        <h4 className={`text-xs font-black uppercase tracking-widest mb-6 ${isNavy ? 'text-blue-400' : 'text-blue-600'
                                            }`}>Included Services</h4>
                                        <ul className="space-y-4">
                                            {service.included.map((inc, i) => (
                                                <li key={i} className={`flex items-start gap-3 text-sm font-semibold ${isNavy ? 'text-slate-200' : 'text-slate-700'
                                                    }`}>
                                                    <ChevronRight size={18} className="text-blue-500 mt-0.5 shrink-0" />
                                                    <span>{inc}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </Section>

            {/* WHY CHOOSE US SECTION */}
            <Section className="bg-[#01014c] text-white relative overflow-hidden">
                <div className="container mx-auto px-6 relative z-10">
                    <div className=" mx-auto text-center mb-20">
                        <span className="text-blue-400 font-bold  text-xs uppercase block mb-3">
                            The Merida Advantage
                        </span>
                        <h2 className="text-3xl md:text-5xl font-bold text-white syne leading-tight mb-4">
                            Why Choose Merida HR Consulting <br />
                            <span className="text-blue-400">Over Traditional Recruitment Firms?</span>
                        </h2>
                        <p className="text-slate-300 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
                            We deliver strategically aligned hiring systems, payroll governance, and AI-enabled sourcing instead of simple transactional resumes.
                        </p>
                    </div>

                    <CardGrid>
                        <PremiumCard>
                            <div className="flex items-center justify-between mb-4">
                                <div className="icon-container w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center text-blue-400 border border-white/10 transition-colors">
                                    <Cpu size={22} className="animate-pulse" />
                                </div>
                                <span className="text-[10px] font-black text-blue-400 uppercase tracking-widest bg-blue-500/10 px-2.5 py-1 rounded-full border border-blue-500/20">AI-Powered</span>
                            </div>
                            <h3 className="text-xl font-bold syne mb-3 text-white">AI-Powered Sourcing</h3>
                            <p className="text-slate-300 text-sm leading-relaxed mb-6">
                                We leverage AI-supported recruitment workflows to improve sourcing efficiency, candidate filtering, hiring coordination, and recruitment scalability.
                            </p>
                            <ul className="space-y-2.5 text-xs font-semibold text-slate-300 mt-auto pt-4 border-t border-white/5">
                                <li className="flex items-center gap-2">✓ Reduce screening delays</li>
                                <li className="flex items-center gap-2">✓ Improve candidate matching</li>
                                <li className="flex items-center gap-2">✓ Streamline workflow coordination</li>
                            </ul>
                        </PremiumCard>

                        <PremiumCard>
                            <div className="flex items-center justify-between mb-4">
                                <div className="icon-container w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center text-blue-400 border border-white/10 transition-colors">
                                    <Building size={22} />
                                </div>
                                <span className="text-[10px] font-black text-blue-400 uppercase tracking-widest bg-blue-500/10 px-2.5 py-1 rounded-full border border-blue-500/20">Specialized</span>
                            </div>
                            <h3 className="text-xl font-bold syne mb-3 text-white">Industry Sourcing Teams</h3>
                            <p className="text-slate-300 text-sm leading-relaxed">
                                Unlike generic staffing firms, our recruiters specialize in industry-focused hiring ecosystems across IT, BFSI, Healthcare, Manufacturing, E-commerce, Logistics, Startups, and GCCs.
                            </p>
                            <p className="text-xs text-slate-400 mt-auto pt-4 border-t border-white/5">Ensures access to role-ready and operationally aligned talent.</p>
                        </PremiumCard>

                        <PremiumCard>
                            <div className="flex items-center justify-between mb-4">
                                <div className="icon-container w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center text-blue-400 border border-white/10 transition-colors">
                                    <Layers size={22} />
                                </div>
                                <span className="text-[10px] font-black text-blue-400 uppercase tracking-widest bg-blue-500/10 px-2.5 py-1 rounded-full border border-blue-500/20">Full Suite</span>
                            </div>
                            <h3 className="text-xl font-bold syne mb-3 text-white">Workforce Lifecycle</h3>
                            <p className="text-slate-300 text-sm leading-relaxed">
                                Most recruitment agencies stop after onboarding. We support businesses across the complete workforce lifecycle, including staffing, RPO, payroll outsourcing, statutory compliance, HR administration, contractor payroll, documentation, and scalable HR operations.
                            </p>
                        </PremiumCard>

                        <PremiumCard>
                            <div className="flex items-center justify-between mb-4">
                                <div className="icon-container w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center text-blue-400 border border-white/10 transition-colors">
                                    <TrendingUp size={22} />
                                </div>
                                <span className="text-[10px] font-black text-blue-400 uppercase tracking-widest bg-blue-500/10 px-2.5 py-1 rounded-full border border-blue-500/20">High Growth</span>
                            </div>
                            <h3 className="text-xl font-bold syne mb-3 text-white">Aggressive Scaling</h3>
                            <p className="text-slate-300 text-sm leading-relaxed">
                                Our workforce systems are designed for businesses experiencing aggressive hiring demand, rapid scaling, operational expansion, multi-location workforce growth, GCC setup, or startup team building. We help organizations scale faster without workforce instability.
                            </p>
                        </PremiumCard>

                        <PremiumCard>
                            <div className="flex items-center justify-between mb-4">
                                <div className="icon-container w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center text-blue-400 border border-white/10 transition-colors">
                                    <Zap size={22} />
                                </div>
                                <span className="text-[10px] font-black text-blue-400 uppercase tracking-widest bg-blue-500/10 px-2.5 py-1 rounded-full border border-blue-500/20">Efficient</span>
                            </div>
                            <h3 className="text-xl font-bold syne mb-3 text-white">Structured Workflows</h3>
                            <p className="text-slate-300 text-sm leading-relaxed">
                                Unstructured hiring creates delays and inefficiencies. Our streamlined recruitment execution systems help businesses reduce hiring turnaround time, improve recruitment visibility, manage bulk hiring efficiently, and create smoother candidate experiences.
                            </p>
                        </PremiumCard>

                        <PremiumCard>
                            <div className="flex items-center justify-between mb-4">
                                <div className="icon-container w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center text-blue-400 border border-white/10 transition-colors">
                                    <ShieldCheck size={22} />
                                </div>
                                <span className="text-[10px] font-black text-blue-400 uppercase tracking-widest bg-blue-500/10 px-2.5 py-1 rounded-full border border-blue-500/20">Compliance</span>
                            </div>
                            <h3 className="text-xl font-bold syne mb-3 text-white">Payroll & Compliance</h3>
                            <p className="text-slate-300 text-sm leading-relaxed">
                                Payroll errors and compliance gaps can create operational and financial risks. Our workforce management systems help businesses maintain accurate payroll processing, statutory compliance, PF & ESI management, contractor payroll workflows, and HR documentation consistency.
                            </p>
                        </PremiumCard>
                    </CardGrid>

                    <div className="mt-16 text-center">
                        <button className="btn-12" onClick={scrollToForm}>
                            <span>Discuss Your HR Needs <ArrowRight size={18} className="arrow" /></span>
                        </button>
                    </div>
                </div>
                <div className="absolute top-0 right-0 w-80 h-80 bg-blue-600/10 rounded-full blur-[100px] pointer-events-none"></div>
                <div className="absolute bottom-0 left-0 w-80 h-80 bg-blue-500/10 rounded-full blur-[100px] pointer-events-none"></div>
            </Section>

            {/* STRATEGIC WORKFORCE EXECUTION FRAMEWORK */}
            <Section className="bg-white">
                <div className="container mx-auto px-6">
                    <div className="max-w-4xl mx-auto text-center mb-16">
                        <span className="text-[#6E97F2] font-bold tracking-[0.3em] text-[10px] uppercase block mb-3">
                            Execution System
                        </span>
                        <h2 className="text-3xl md:text-5xl font-bold text-slate-900 syne leading-tight mb-6">
                            Our Strategic Workforce <br />
                            <span className="text-[#6E97F2]">Execution Framework</span>
                        </h2>
                        <p className="text-slate-500 text-lg leading-relaxed">
                            Most staffing companies focus only on filling positions. Our workforce consulting systems are built around long-term hiring efficiency, workforce quality, operational scalability, and business continuity.
                        </p>
                    </div>

                    {/* Timeline Flow */}
                    <div className="relative max-w-6xl mx-auto">
                        <div className="absolute left-8 lg:left-1/2 top-0 bottom-0 w-0.5 bg-slate-200 transform lg:-translate-x-1/2 hidden md:block"></div>

                        <div className="space-y-6">
                            {frameworkSteps.map((step, idx) => {
                                const isEven = idx % 2 === 0;
                                return (
                                    <div key={idx} className="relative flex flex-col lg:flex-row items-start w-full">
                                        {/* Timeline dot */}
                                        <div className="absolute left-8 lg:left-1/2 w-5 h-5 rounded-full bg-[#6E97F2] border-4 border-white transform lg:-translate-x-1/2 mt-6 hidden md:block z-10"></div>

                                        {!isEven ? (
                                            <>
                                                {/* Card on Left */}
                                                <div className="w-full lg:w-1/2 pl-12 lg:pl-0 lg:pr-16">
                                                    <motion.div
                                                        initial={{ opacity: 0, x: -120, scale: 0.88 }}
                                                        whileInView={{ opacity: 1, x: 0, scale: 1 }}
                                                        viewport={{ once: true, margin: "-80px" }}
                                                        transition={{ type: "spring", stiffness: 60, damping: 10 }}
                                                        className="bg-gradient-to-br from-[#2563EB] to-[#1E40AF] border-2 border-[#6E97F2]/30 p-6 rounded-3xl hover:border-white/50 transition-all duration-300 shadow-md hover:shadow-xl hover:shadow-[#6E97F2]/25"
                                                    >
                                                        <span className="text-[#6E97F2] text-xs font-black tracking-widest block mb-2 text-white">{step.step}</span>
                                                        <h3 className="text-xl font-bold text-white syne mb-2">{step.title}</h3>
                                                        <p className="text-white text-sm leading-relaxed">{step.desc}</p>
                                                    </motion.div>
                                                </div>
                                                <div className="w-full lg:w-1/2 hidden lg:block"></div>
                                            </>
                                        ) : (
                                            <>
                                                {/* Card on Right */}
                                                <div className="w-full lg:w-1/2 hidden lg:block"></div>
                                                <div className="w-full lg:w-1/2 pl-12 lg:pl-16 lg:pr-0">
                                                    <motion.div
                                                        initial={{ opacity: 0, x: 120, scale: 0.88 }}
                                                        whileInView={{ opacity: 1, x: 0, scale: 1 }}
                                                        viewport={{ once: true, margin: "-80px" }}
                                                        transition={{ type: "spring", stiffness: 60, damping: 10 }}
                                                        className="bg-gradient-to-br from-[#2563EB] to-[#1E40AF] border-2 border-[#6E97F2]/30 p-6 rounded-3xl hover:border-white/50 transition-all duration-300 shadow-md hover:shadow-xl hover:shadow-[#6E97F2]/25"
                                                    >
                                                        <span className="text-[#6E97F2] text-xs font-black tracking-widest block mb-2 text-white">{step.step}</span>
                                                        <h3 className="text-xl font-bold text-white syne mb-2">{step.title}</h3>
                                                        <p className="text-white text-sm leading-relaxed">{step.desc}</p>
                                                    </motion.div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    
                    <div className="mt-16 text-center">
                        <CTAButton
                            text="Discuss Your Workforce Strategy"
                            onClick={scrollToForm}
                        />
                    </div>
                </div>
            </Section>

            {/* INDUSTRY-FOCUSED SECTION */}
            <Section className="bg-gradient-to-b from-blue-50/90 via-blue-100/40 to-white">
                <div className="container mx-auto px-6">
                    <div className="max-w-4xl mx-auto text-center mb-16">
                        <span className="text-blue-600 font-bold tracking-[0.3em] text-[10px] uppercase block mb-3">
                            Sector Expertise
                        </span>
                        <h2 className="text-3xl md:text-5xl font-bold text-slate-900 syne leading-tight mb-6">
                            Industry-Focused <br />
                            <span className="text-blue-600">HR Consulting Services</span>
                        </h2>
                        <p className="text-slate-600 text-base md:text-lg leading-relaxed">
                            Different industries require different hiring strategies, workforce structures, compliance systems, and recruitment execution models. Our specialized hiring teams understand industry-specific talent expectations, workforce challenges, and operational hiring dynamics across multiple sectors.
                        </p>
                    </div>

                    <motion.div
                        variants={{
                            hidden: { opacity: 0 },
                            visible: {
                                opacity: 1,
                                transition: {
                                    staggerChildren: 0.1
                                }
                            }
                        }}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-80px" }}
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12"
                    >
                        {industryItems.map((item, idx) => (
                            <motion.div
                                key={idx}
                                variants={{
                                    hidden: { opacity: 0.2, y: 40, scale: 0.95 },
                                    visible: {
                                        opacity: 1,
                                        y: 0,
                                        scale: 1,
                                        transition: {
                                            type: "spring",
                                            stiffness: 70,
                                            damping: 14
                                        }
                                    }
                                }}
                                className={`relative overflow-hidden rounded-[2.5rem] p-8 md:p-10 min-h-[350px] flex flex-col justify-end border border-slate-200/50 shadow-sm transition-all duration-500 hover:-translate-y-2.5 hover:shadow-2xl hover:shadow-[#01014c]/20 hover:border-[#6E97F2]/50 group cursor-pointer ${item.span}`}
                            >
                                {/* Zoomable Background Image Container */}
                                <div
                                    className="absolute inset-0 transition-transform duration-700 ease-out group-hover:scale-110"
                                    style={{
                                        backgroundImage: `linear-gradient(rgba(1, 1, 76, 0.25), rgba(1, 1, 76, 0.7)), url(${item.image})`,
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center'
                                    }}
                                />

                                {/* Spotlight Shine Overlay */}
                                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-in-out pointer-events-none" />
                                <div className="absolute inset-0 bg-[#6E97F2]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

                                <div className="relative z-10 space-y-3">
                                    <div className="flex items-center justify-between">
                                        <h3 className="text-xl md:text-2xl font-bold text-white syne group-hover:text-[#6E97F2] transition-colors">{item.name}</h3>
                                        <ArrowRight size={22} className="text-white/60 group-hover:text-[#6E97F2] group-hover:translate-x-1.5 transition-all duration-300" />
                                    </div>
                                    <p className="text-slate-200 text-xs md:text-sm leading-relaxed font-sans opacity-90 group-hover:opacity-100 transition-opacity">{item.desc}</p>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>

                    <div className="text-center">
                        <button className="btn-12-navy" onClick={() => navigate('/industry')}>
                            <span>Explore Industries <ArrowRight size={18} className="arrow" /></span>
                        </button>
                    </div>
                </div>
            </Section>

            {/* OUTCOME METRICS SECTION */}
            <Section className="bg-white">
                <div className="container mx-auto px-6">
                    <div className="max-w-4xl mx-auto text-center mb-20">
                        <span className="text-blue-600 font-bold tracking-[0.3em] text-[10px] uppercase block mb-3">
                            Impact Metrics
                        </span>
                        <h2 className="text-3xl md:text-5xl font-bold text-slate-900 syne leading-tight mb-6">
                            HR Solutions Focused On <br />
                            <span className="text-blue-600">Measurable Business Outcomes</span>
                        </h2>
                        <p className="text-slate-500 text-lg leading-relaxed">
                            Every workforce decision directly impacts operational efficiency, productivity, scalability, employee retention, and business growth. That is why our HR consulting services are designed around measurable performance outcomes instead of generic hiring activities.
                        </p>
                    </div>

                    <motion.div
                        variants={{
                            hidden: { opacity: 0 },
                            visible: {
                                opacity: 1,
                                transition: {
                                    staggerChildren: 0.1
                                }
                            }
                        }}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-80px" }}
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16"
                    >
                        {outcomesList.map((out, idx) => {
                            const isBlueCard = idx % 2 === 1;
                            return (
                                <motion.div
                                    key={idx}
                                    variants={{
                                        hidden: { opacity: 0, y: 30, scale: 0.95 },
                                        visible: {
                                            opacity: 1,
                                            y: 0,
                                            scale: 1,
                                            transition: {
                                                type: "spring",
                                                stiffness: 80,
                                                damping: 15
                                            }
                                        }
                                    }}
                                    className={`p-8 rounded-[2rem] border transition-all duration-500 hover:-translate-y-2.5 hover:shadow-xl group cursor-pointer ${isBlueCard
                                            ? 'bg-gradient-to-br from-[#2563EB] to-[#1E40AF] border-transparent hover:border-white/50 hover:shadow-blue-500/20'
                                            : 'bg-slate-50/50 border-slate-200/60 hover:border-blue-500/30 hover:bg-white hover:shadow-blue-500/5'
                                        }`}
                                >
                                    <span className={`text-xs font-black tracking-widest block mb-2 uppercase transition-colors ${isBlueCard ? 'text-blue-200 group-hover:text-white' : 'text-blue-600 group-hover:text-blue-500'
                                        }`}>{out.title}</span>
                                    <h3 className={`text-lg font-bold syne mb-4 transition-colors ${isBlueCard ? 'text-white' : 'text-slate-900 group-hover:text-[#01014c]'
                                        }`}>{out.highlight}</h3>
                                    <p className={`text-sm leading-relaxed transition-colors ${isBlueCard ? 'text-white/90' : 'text-slate-500 group-hover:text-slate-600'
                                        }`}>{out.desc}</p>
                                </motion.div>
                            );
                        })}
                    </motion.div>

                    <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                        <button className="btn-12" onClick={scrollToForm}>
                            <span>Build A Smarter Workforce <ArrowRight size={18} className="arrow" /></span>
                        </button>
                        <button
                            className="px-8 py-3.5 border border-[#01014c]/20 hover:border-[#01014c] rounded-full text-[#01014c] font-bold transition-all duration-300 flex items-center gap-2"
                            onClick={scrollToForm}
                        >
                            Request HR Consultation
                        </button>
                    </div>
                </div>
            </Section>

            {/* ENQUIRY FORM SECTION */}
            <Section id="enquiry-section" className="bg-slate-50 relative overflow-hidden">
                <div className="container mx-auto px-6 relative z-10">
                    <div className="bg-[#01014C] rounded-[3.5rem] p-8 md:p-16 shadow-2xl relative overflow-hidden grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                        <div className="lg:col-span-5 text-white space-y-6">
                            <span className="text-blue-400 font-bold tracking-[0.2em] text-[10px] uppercase block">
                                Connect With Experts
                            </span>
                            <h2 className="text-3xl md:text-4xl font-bold syne leading-tight">
                                Speak With Workforce Strategy Experts
                            </h2>
                            <p className="text-blue-100/70 leading-relaxed text-sm">
                                Tell us about your hiring goals, workforce challenges, expansion plans, or HR operational requirements. Our consultants will design customized workforce solutions aligned with your business objectives.
                            </p>
                            <div className="pt-4 space-y-4">
                                <div className="flex items-center gap-3 text-sm text-blue-200">
                                    <CheckCircle2 size={18} className="text-blue-400" />
                                    <span>Personalized strategic roadmap</span>
                                </div>
                                <div className="flex items-center gap-3 text-sm text-blue-200">
                                    <CheckCircle2 size={18} className="text-blue-400" />
                                    <span>Detailed SLAs and costing structures</span>
                                </div>
                            </div>
                        </div>

                        <div className="lg:col-span-7 bg-white/5 border border-white/10 rounded-[2.5rem] p-6 md:p-10 backdrop-blur-md">
                            <form className="grid grid-cols-1 md:grid-cols-2 gap-6" onSubmit={handleSubmit}>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-blue-400 uppercase tracking-widest ml-1">Full Name</label>
                                    <div className="relative">
                                        <User size={16} className="absolute left-4 top-4 text-white/40" />
                                        <input
                                            type="text"
                                            name="Full Name"
                                            value={formData["Full Name"]}
                                            onChange={handleChange}
                                            required
                                            placeholder="John Doe"
                                            className="w-full bg-white/5 border border-white/10 rounded-xl pl-11 pr-5 py-3.5 text-white outline-none focus:border-blue-500 transition-all placeholder:text-white/20 text-sm"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-blue-400 uppercase tracking-widest ml-1">Company Name</label>
                                    <div className="relative">
                                        <Building size={16} className="absolute left-4 top-4 text-white/40" />
                                        <input
                                            type="text"
                                            name="Company Name"
                                            value={formData["Company Name"]}
                                            onChange={handleChange}
                                            required
                                            placeholder="Acme Corp"
                                            className="w-full bg-white/5 border border-white/10 rounded-xl pl-11 pr-5 py-3.5 text-white outline-none focus:border-blue-500 transition-all placeholder:text-white/20 text-sm"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-blue-400 uppercase tracking-widest ml-1">Business Email</label>
                                    <div className="relative">
                                        <Mail size={16} className="absolute left-4 top-4 text-white/40" />
                                        <input
                                            type="email"
                                            name="Business Email"
                                            value={formData["Business Email"]}
                                            onChange={handleChange}
                                            required
                                            placeholder="john@company.com"
                                            className="w-full bg-white/5 border border-white/10 rounded-xl pl-11 pr-5 py-3.5 text-white outline-none focus:border-blue-500 transition-all placeholder:text-white/20 text-sm"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-blue-400 uppercase tracking-widest ml-1">Phone Number</label>
                                    <div className="relative">
                                        <Phone size={16} className="absolute left-4 top-4 text-white/40" />
                                        <input
                                            type="tel"
                                            name="Phone Number"
                                            value={formData["Phone Number"]}
                                            onChange={handleChange}
                                            required
                                            placeholder="10-digit number"
                                            className="w-full bg-white/5 border border-white/10 rounded-xl pl-11 pr-5 py-3.5 text-white outline-none focus:border-blue-500 transition-all placeholder:text-white/20 text-sm"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-blue-400 uppercase tracking-widest ml-1">Services Required</label>
                                    <select
                                        name="Services Required"
                                        value={formData["Services Required"]}
                                        onChange={handleChange}
                                        className="w-full bg-white/5 border border-white/10 rounded-xl px-5 py-3.5 text-white outline-none focus:border-blue-500 transition-all text-sm appearance-none cursor-pointer"
                                    >
                                        <option className="bg-[#01014C] text-white" value="Staffing & Recruitment Services">Staffing & Recruitment Services</option>
                                        <option className="bg-[#01014C] text-white" value="Specialized Recruitment Services">Specialized Recruitment Services</option>
                                        <option className="bg-[#01014C] text-white" value="Recruitment Process Outsourcing (RPO) Services">Recruitment Process Outsourcing (RPO) Services</option>
                                        <option className="bg-[#01014C] text-white" value="HR Outsourcing Services">HR Outsourcing Services</option>
                                        <option className="bg-[#01014C] text-white" value="Payroll & Compliance Services">Payroll & Compliance Services</option>
                                        <option className="bg-[#01014C] text-white" value="Statutory & Compliance Management Services">Statutory & Compliance Management Services</option>
                                    </select>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-blue-400 uppercase tracking-widest ml-1">Hiring Volume</label>
                                    <select
                                        name="Hiring Volume"
                                        value={formData["Hiring Volume"]}
                                        onChange={handleChange}
                                        className="w-full bg-white/5 border border-white/10 rounded-xl px-5 py-3.5 text-white outline-none focus:border-blue-500 transition-all text-sm appearance-none cursor-pointer"
                                    >
                                        <option className="bg-[#01014C] text-white" value="1-10 Positions">1-10 Positions</option>
                                        <option className="bg-[#01014C] text-white" value="10-50 Positions">10-50 Positions</option>
                                        <option className="bg-[#01014C] text-white" value="50-100 Positions">50-100 Positions</option>
                                        <option className="bg-[#01014C] text-white" value="100+ Positions">100+ Positions</option>
                                    </select>
                                </div>

                                <div className="md:col-span-2 space-y-2">
                                    <label className="text-[10px] font-black text-blue-400 uppercase tracking-widest ml-1">Message</label>
                                    <textarea
                                        rows={4}
                                        name="Message"
                                        value={formData["Message"]}
                                        onChange={handleChange}
                                        placeholder="Briefly describe your requirements..."
                                        className="w-full bg-white/5 border border-white/10 rounded-xl px-5 py-3.5 text-white outline-none focus:border-blue-500 transition-all text-sm placeholder:text-white/20"
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={loading}
                                    className={`md:col-span-2 py-4 ${loading ? 'bg-blue-700 text-white cursor-not-allowed' : 'bg-blue-600 hover:bg-white hover:text-[#01014C] text-white'} font-bold uppercase tracking-widest rounded-xl transition-all shadow-xl flex justify-center items-center text-sm`}
                                >
                                    {loading ? (
                                        <span className="animate-pulse">Submitting Request...</span>
                                    ) : (
                                        "Get Custom HR Services"
                                    )}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </Section>

            {/* LOCATIONS SECTION */}
            <Section className="bg-white">
                <div className="container mx-auto px-6 text-center max-w-5xl">
                    <span className="text-blue-600 font-bold tracking-[0.3em] text-[10px] uppercase block mb-3">
                        Regional Hubs
                    </span>
                    <h2 className="text-3xl md:text-5xl font-bold text-slate-900 syne leading-tight mb-8">
                        HR Consulting Services Across <br />
                        <span className="text-blue-600">Major Business Hubs In India</span>
                    </h2>

                    <p className="text-slate-600 leading-relaxed font-sans text-base md:text-lg mb-8 text-center max-w-4xl mx-auto">
                        As a trusted HR consulting company in Bangalore, we support organizations across multiple cities with scalable recruitment, payroll, compliance, and staffing solutions. Our regional hiring expertise enables businesses to access better talent pipelines, localized workforce support, and faster recruitment execution.
                    </p>

                    {/* Cities link grid */}
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-10 max-w-4xl mx-auto">
                        {["Bangalore", "Chennai", "Hyderabad", "Pune", "Mumbai", "Delhi NCR"].map((city, idx) => (
                            <div 
                                key={idx} 
                                className="px-4 py-3 bg-slate-50 border border-slate-200/60 rounded-2xl flex items-center justify-center gap-2 hover:border-blue-500/30 transition-all duration-300 group cursor-pointer" 
                                onClick={() => {
                                    const locSlug = city.toLowerCase().replace(' ncr', '');
                                    navigate(`/locations/${locSlug}`);
                                }}
                            >
                                <MapPin size={16} className="text-blue-500 group-hover:scale-110 transition-transform" />
                                <span className="text-xs font-bold text-slate-700">{city}</span>
                            </div>
                        ))}
                    </div>

                    <p className="text-slate-400 leading-relaxed font-sans text-xs md:text-sm max-w-3xl mx-auto mb-10">
                        Businesses searching for reliable HR consulting services in Bangalore, experienced staffing companies in Jayanagar, or scalable recruitment firms near me can leverage our customized workforce solutions tailored for local hiring needs and industry-specific requirements.
                    </p>

                    <button className="btn-12" onClick={() => navigate('/contact')}>
                        <span>Explore Locations <ArrowRight size={18} className="arrow" /></span>
                    </button>
                </div>
            </Section>

            <Section className="bg-slate-50">
                <div className="container mx-auto px-6 text-center">
                    <span className="text-blue-600 font-bold tracking-[0.3em] text-[10px] uppercase block mb-3">
                        Client Success
                    </span>
                    <h2 className="text-3xl md:text-5xl font-bold text-slate-900 syne leading-tight mb-4">
                        What Businesses Say About <br />
                        <span className="text-blue-600">Our HR Consulting Services</span>
                    </h2>
                    <p className="text-slate-500 text-sm md:text-base max-w-2xl mx-auto leading-relaxed mb-16">
                        Hear from client leaders and operational managers who have successfully scaled their teams and operations using our platforms.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left max-w-6xl mx-auto">
                        <div className="bg-white p-8 rounded-[2rem] border border-slate-200/60 shadow-sm relative">
                            <div className="flex gap-1 text-amber-400 mb-4">
                                {[...Array(5)].map((_, i) => <Star key={i} size={16} fill="currentColor" />)}
                            </div>
                            <p className="text-slate-600 text-sm leading-relaxed mb-6 font-sans">
                                "Merida's specialized hiring teams in Bangalore helped us set up our IT engineering hub and onboard 30 product engineers in under 6 weeks. Their screening and interview coordination were seamless."
                            </p>
                            <div>
                                <h4 className="font-extrabold text-slate-900 text-sm">VP Engineering</h4>
                                <span className="text-xs text-slate-400">Fintech Enterprise</span>
                            </div>
                        </div>

                        <div className="bg-white p-8 rounded-[2rem] border border-slate-200/60 shadow-sm relative">
                            <div className="flex gap-1 text-amber-400 mb-4">
                                {[...Array(5)].map((_, i) => <Star key={i} size={16} fill="currentColor" />)}
                            </div>
                            <p className="text-slate-600 text-sm leading-relaxed mb-6 font-sans">
                                "We transitioned our entire contract staffing and ESI compliance management to Merida HR. The administrative burden on our internal HR dropped significantly, and compliance audits have been error-free."
                            </p>
                            <div>
                                <h4 className="font-extrabold text-slate-900 text-sm">HR Director</h4>
                                <span className="text-xs text-slate-400">Logistics Multinational</span>
                            </div>
                        </div>

                        <div className="bg-white p-8 rounded-[2rem] border border-slate-200/60 shadow-sm relative">
                            <div className="flex gap-1 text-amber-400 mb-4">
                                {[...Array(5)].map((_, i) => <Star key={i} size={16} fill="currentColor" />)}
                            </div>
                            <p className="text-slate-600 text-sm leading-relaxed mb-6 font-sans">
                                "As a fast-scaling startup, managing payroll while hiring aggressively was chaotic. Merida provided a combined RPO and payroll solution that gave us the operational infrastructure to scale securely."
                            </p>
                            <div>
                                <h4 className="font-extrabold text-slate-900 text-sm">Co-Founder & COO</h4>
                                <span className="text-xs text-slate-400">SaaS Startup</span>
                            </div>
                        </div>
                    </div>
                </div>
            </Section>

            {/* FAQ SECTION */}
            <Section className="bg-white">
                <div className="container mx-auto px-6 max-w-4xl">
                    <div className="text-center mb-16">
                        <span className="text-blue-600 font-bold tracking-[0.3em] text-[10px] uppercase block mb-3">
                            Information Center
                        </span>
                        <h2 className="text-3xl md:text-5xl font-bold text-slate-900 syne leading-tight mb-4">
                            Frequently Asked Questions
                        </h2>
                        <p className="text-slate-500 text-sm md:text-base leading-relaxed">
                            Clear explanations of our recruitment timelines, pricing models, payroll transitions, and compliance integration processes.
                        </p>
                    </div>

                    <div className="space-y-4">
                        {faqs.map((faq, idx) => {
                            const isOpen = openFaq === idx;
                            return (
                                <div key={idx} className="border border-slate-200 rounded-2xl overflow-hidden transition-all duration-300">
                                    <button
                                        className="w-full flex justify-between items-center px-5 py-3.5 bg-slate-50 hover:bg-slate-100/50 text-left transition-colors"
                                        onClick={() => toggleFaq(idx)}
                                    >
                                        <span className="font-bold text-[#01014c] text-sm md:text-base pr-4">{faq.q}</span>
                                        <motion.div
                                            animate={{ rotate: isOpen ? 180 : 0 }}
                                            transition={{ duration: 0.3 }}
                                            className="shrink-0 text-blue-500"
                                        >
                                            <ChevronDown size={20} />
                                        </motion.div>
                                    </button>

                                    <AnimatePresence initial={false}>
                                        {isOpen && (
                                            <motion.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: "auto", opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{ duration: 0.3 }}
                                            >
                                                <div className="px-5 py-3.5 border-t border-slate-100 text-slate-600 text-sm leading-relaxed font-sans bg-white">
                                                    {faq.a}
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </Section>

            {/* FINAL CTA REPLACED WITH BANNER */}
            <section className="py-5 bg-white relative overflow-hidden ">
                <div className="container mx-auto px-6 relative z-10">
                    <div className=" mx-auto text-center">
                        <div className="w-full rounded-[2rem] overflow-hidden shadow-2xl mb-10 border border-slate-200">
                            <img 
                                src={recruitmentImg} 
                                alt="Recruitment Consulting Firm" 
                                className="w-full h-auto object-cover"
                            />
                        </div>
                        <div className="flex justify-center">
                            <CTAButton
                                text="Book HR Consultation"
                                onClick={scrollToForm}
                            />
                        </div>
                    </div>
                </div>
            </section>
        </PageWrapper>
    );
};

const PageWrapper = styled.div`
  background-color: #ffffff;

  .btn-12,
  .btn-12 *,
  .btn-12 :after,
  .btn-12 :before,
  .btn-12:after,
  .btn-12:before {
    border: 0 solid;
    box-sizing: border-box;
  }

  .btn-12 {
    -webkit-tap-highlight-color: transparent;
    -webkit-appearance: button;
    background-color: #3b82f6;
    background-image: none;
    color: #fff;
    cursor: pointer;
    font-family: 'Syne', sans-serif;
    font-size: 15px;
    font-weight: 700;
    line-height: 1.5;
    margin: 0;
    -webkit-mask-image: -webkit-radial-gradient(#3b82f6, #fff);
    padding: 0;
    text-transform: uppercase;
    border-radius: 99rem;
    border-width: 2px;
    overflow: hidden;
    padding: 0.9rem 2.8rem;
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-color: #3b82f6;
    transition: border-color 0.2s ease, color 0.2s ease;
  }

  .btn-12:hover {
    border-color: #3b82f6;
    color: #3b82f6;
  }

  .btn-12 span {
    display: flex;
    align-items: center;
    gap: 10px;
    z-index: 2;
    transition: color 0.2s ease;
  }

  .btn-12 span .arrow {
    transition: transform 0.3s ease;
  }

  .btn-12:hover span .arrow {
    transform: translateX(4px);
  }

  .btn-12:after,
  .btn-12:before {
    background: linear-gradient(
      90deg,
      #fff 25%,
      transparent 0,
      transparent 50%,
      #fff 0,
      #fff 75%,
      transparent 0
    );
    content: "";
    inset: 0;
    position: absolute;
    transform: translateY(var(--progress, 105%));
    transition: transform 0.2s ease;
    z-index: 1;
  }

  .btn-12:after {
    --progress: -105%;
    background: linear-gradient(
      90deg,
      transparent 0,
      transparent 25%,
      #fff 0,
      #fff 50%,
      transparent 0,
      transparent 75%,
      #fff 0
    );
    z-index: 1;
  }

  .btn-12:hover:after,
  .btn-12:hover:before {
    --progress: 0;
  }

  .btn-12-white,
  .btn-12-white *,
  .btn-12-white :after,
  .btn-12-white :before,
  .btn-12-white:after,
  .btn-12-white:before {
    border: 0 solid;
    box-sizing: border-box;
  }

  .btn-12-white {
    -webkit-tap-highlight-color: transparent;
    -webkit-appearance: button;
    background-color: #ffffff;
    background-image: none;
    color: #01014c;
    cursor: pointer;
    font-family: 'Syne', sans-serif;
    font-size: 15px;
    font-weight: 700;
    line-height: 1.5;
    margin: 0;
    -webkit-mask-image: -webkit-radial-gradient(#ffffff, #fff);
    padding: 0;
    text-transform: uppercase;
    border-radius: 99rem;
    border-width: 2px;
    overflow: hidden;
    padding: 0.9rem 2.8rem;
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-color: #ffffff;
    transition: border-color 0.2s ease, color 0.2s ease;
  }

  .btn-12-white:hover {
    border-color: #01014c;
    color: #ffffff;
  }

  .btn-12-white span {
    display: flex;
    align-items: center;
    gap: 10px;
    z-index: 2;
    transition: color 0.2s ease;
  }

  .btn-12-white span .arrow {
    transition: transform 0.3s ease;
  }

  .btn-12-white:hover span .arrow {
    transform: translateX(4px);
  }

  .btn-12-white:after,
  .btn-12-white:before {
    background: linear-gradient(
      90deg,
      #01014c 25%,
      transparent 0,
      transparent 50%,
      #01014c 0,
      #01014c 75%,
      transparent 0
    );
    content: "";
    inset: 0;
    position: absolute;
    transform: translateY(var(--progress, 105%));
    transition: transform 0.2s ease;
    z-index: 1;
  }

  .btn-12-white:after {
    --progress: -105%;
    background: linear-gradient(
      90deg,
      transparent 0,
      transparent 25%,
      #01014c 0,
      #01014c 50%,
      transparent 0,
      transparent 75%,
      #01014c 0
    );
    z-index: 1;
  }

  .btn-12-white:hover:after,
  .btn-12-white:hover:before {
    --progress: 0;
  }

  .btn-12-navy,
  .btn-12-navy *,
  .btn-12-navy :after,
  .btn-12-navy :before,
  .btn-12-navy:after,
  .btn-12-navy:before {
    border: 0 solid;
    box-sizing: border-box;
  }

  .btn-12-navy {
    -webkit-tap-highlight-color: transparent;
    -webkit-appearance: button;
    background-color: #01014c;
    background-image: none;
    color: #fff;
    cursor: pointer;
    font-family: 'Syne', sans-serif;
    font-size: 15px;
    font-weight: 700;
    line-height: 1.5;
    margin: 0;
    -webkit-mask-image: -webkit-radial-gradient(#01014c, #fff);
    padding: 0;
    text-transform: uppercase;
    border-radius: 99rem;
    border-width: 2px;
    overflow: hidden;
    padding: 0.9rem 2.8rem;
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-color: #01014c;
    transition: border-color 0.2s ease, color 0.2s ease;
  }

  .btn-12-navy:hover {
    border-color: #01014c;
    color: #01014c;
  }

  .btn-12-navy span {
    display: flex;
    align-items: center;
    gap: 10px;
    z-index: 2;
    transition: color 0.2s ease;
  }

  .btn-12-navy span .arrow {
    transition: transform 0.3s ease;
  }

  .btn-12-navy:hover span .arrow {
    transform: translateX(4px);
  }

  .btn-12-navy:after,
  .btn-12-navy:before {
    background: linear-gradient(
      90deg,
      #fff 25%,
      transparent 0,
      transparent 50%,
      #fff 0,
      #fff 75%,
      transparent 0
    );
    content: "";
    inset: 0;
    position: absolute;
    transform: translateY(var(--progress, 105%));
    transition: transform 0.2s ease;
    z-index: 1;
  }

  .btn-12-navy:after {
    --progress: -105%;
    background: linear-gradient(
      90deg,
      transparent 0,
      transparent 25%,
      #fff 0,
      #fff 50%,
      transparent 0,
      transparent 75%,
      #fff 0
    );
    z-index: 1;
  }

  .btn-12-navy:hover:after,
  .btn-12-navy:hover:before {
    --progress: 0;
  }
`;

const Section = styled.section`
  padding: 100px 0;
  
  @media (max-width: 768px) {
    padding: 70px 0;
  }
`;

const HeroSection = styled.section`
  position: relative;
  background-color: #ffffff;
  padding: 100px 0 100px 0;
  overflow: hidden;

  /* Subtle technical dots grid overlay (brand-tinted for light theme) */
  background-image: radial-gradient(rgba(37, 99, 235, 0.04) 1px, transparent 1px);
  background-size: 36px 36px;
  
  @media (max-width: 768px) {
    padding: 90px 0 70px 0;
  }

  @keyframes float-slow-1 {
    0%, 100% { transform: translate(0, 0) scale(1); }
    50% { transform: translate(30px, -30px) scale(1.05); }
  }

  @keyframes float-slow-2 {
    0%, 100% { transform: translate(0, 0) scale(1.05); }
    50% { transform: translate(-30px, 20px) scale(0.95); }
  }

  .animate-float-slow-1 {
    animation: float-slow-1 15s ease-in-out infinite;
  }

  .animate-float-slow-2 {
    animation: float-slow-2 18s ease-in-out infinite;
  }
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.5rem;
  width: 100%;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }
`;

const PremiumCard = styled.div`
  position: relative;
  border-radius: 2rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  padding: 1.75rem;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  min-height: 250px;
  overflow: hidden;
  cursor: pointer;
  
  /* GPU-accelerated smooth transformations */
  transition: transform 0.3s cubic-bezier(0.25, 1, 0.5, 1), 
              border-color 0.3s cubic-bezier(0.25, 1, 0.5, 1), 
              box-shadow 0.3s cubic-bezier(0.25, 1, 0.5, 1),
              background-color 0.3s cubic-bezier(0.25, 1, 0.5, 1);

  &:hover {
    transform: translateY(-6px);
    background-color: rgba(255, 255, 255, 0.06);
    border-color: rgba(59, 130, 246, 0.5);
    box-shadow: 0 20px 40px -10px rgba(59, 130, 246, 0.25);
  }

  .icon-container {
    transition: transform 0.3s cubic-bezier(0.25, 1, 0.5, 1), 
                background-color 0.3s ease, 
                border-color 0.3s ease;
  }

  &:hover .icon-container {
    transform: scale(1.1);
    background-color: rgba(59, 130, 246, 0.15);
    border-color: rgba(59, 130, 246, 0.3);
  }
  
  h3 {
    transition: color 0.3s ease;
  }
  
  &:hover h3 {
    color: #60a5fa;
  }
`;

export default MainServicesPage;
